import React from 'react';
import styled from 'styled-components';
import { Button, TabButton, IconButtonWrapper } from '@prosolve/common-components-ui';
import { FaRegTrashAlt, FaArrowUp, FaArrowDown, FaPlus } from 'react-icons/fa';
import ConditionEditor from './ConditionEditor';
import AssessmentNamePicker from './AssessmentNamePicker';
import ModNamePicker from './ModNamePicker';

const RuleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  padding: 15px;
  border: 1px solid #c4c4c4;
  border-radius: 5px;
`

const RuleLabel = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #565656;
`

const FieldLabel = styled.div`
  font-size: 14px;
  color: #565656;
`

export default ({
  rule = {},
  index,
  totalRules,
  questions = [],
  assessmentNames = [],
  modsByAssessment = {},
  currentAssessmentName,
  onChange,
  onDelete,
  onMove,
  isReadOnly
}) => {
  const { conjunction = 'AND', conditions = [], followupAssessmentName = '', followupAssessmentMod = '' } = rule;

  const targetModOptions = modsByAssessment[followupAssessmentName] || [];

  const onConditionChanged = (conditionIndex, updatedCondition) => {
    const updatedConditions = [...conditions];
    updatedConditions[conditionIndex] = updatedCondition;
    onChange({ ...rule, conditions: updatedConditions })
  }

  const onConditionDeleted = (conditionIndex) => {
    const updatedConditions = conditions.filter((c, i) => i !== conditionIndex);
    onChange({ ...rule, conditions: updatedConditions })
  }

  const addNewCondition = () => {
    const newCondition = { questionId: '', operator: 'any', optionIds: [] };
    onChange({ ...rule, conditions: [...conditions, newCondition] })
  }

  return (
    <RuleWrapper>
      <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
        <RuleLabel>{`Rule ${index + 1}`}</RuleLabel>
        <div style={{ flex: 1 }} />
        {
          !isReadOnly &&
          <>
            <IconButtonWrapper
              onClick={() => index > 0 && onMove(index, index - 1)}
              title='Move rule up'
              style={{ opacity: index > 0 ? 1 : 0.3 }}
            >
              <FaArrowUp style={{ color: '#565656' }} />
            </IconButtonWrapper>
            <IconButtonWrapper
              onClick={() => index < (totalRules - 1) && onMove(index, index + 1)}
              title='Move rule down'
              style={{ opacity: index < (totalRules - 1) ? 1 : 0.3 }}
            >
              <FaArrowDown style={{ color: '#565656' }} />
            </IconButtonWrapper>
            <IconButtonWrapper onClick={onDelete} title='Remove rule'>
              <FaRegTrashAlt style={{ color: '#565656' }} />
            </IconButtonWrapper>
          </>
        }
      </div>

      {
        conditions.length > 1 &&
        <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px' }}>
          <FieldLabel>{'Match'}</FieldLabel>
          <TabButton
            $selected={conjunction === 'AND'}
            onClick={() => !isReadOnly && onChange({ ...rule, conjunction: 'AND' })}
          >
            {'ALL conditions (AND)'}
          </TabButton>
          <TabButton
            $selected={conjunction === 'OR'}
            onClick={() => !isReadOnly && onChange({ ...rule, conjunction: 'OR' })}
          >
            {'ANY condition (OR)'}
          </TabButton>
        </div>
      }

      <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
        {
          conditions.map((condition, conditionIndex) => (
            <ConditionEditor
              key={conditionIndex}
              condition={condition}
              questions={questions}
              onChange={(updatedCondition) => onConditionChanged(conditionIndex, updatedCondition)}
              onDelete={() => onConditionDeleted(conditionIndex)}
              isReadOnly={isReadOnly}
            />
          ))
        }
      </div>

      {
        !isReadOnly &&
        <Button style={{ display: 'inline-flex', alignSelf: 'flex-start' }} onClick={addNewCondition}>
          <FaPlus style={{ fontSize: '10px', marginRight: '5px' }} />
          {'Add Condition'}
        </Button>
      }

      <div style={{ display: 'flex', alignItems: 'center', columnGap: '10px', flexWrap: 'wrap', rowGap: '10px' }}>
        <FieldLabel>{'Then send the student to'}</FieldLabel>
        <AssessmentNamePicker
          value={followupAssessmentName}
          assessmentNames={assessmentNames}
          excludeName={currentAssessmentName}
          emptyText='Select an assessment...'
          onChange={(name) => onChange({ ...rule, followupAssessmentName: name, followupAssessmentMod: '' })}
          disabled={isReadOnly}
        />
        {
          Boolean(targetModOptions.length) &&
          <>
            <FieldLabel>{'with mod'}</FieldLabel>
            <ModNamePicker
              value={followupAssessmentMod}
              modOptions={targetModOptions}
              onChange={(mod) => onChange({ ...rule, followupAssessmentMod: mod })}
              disabled={isReadOnly}
            />
          </>
        }
      </div>
    </RuleWrapper>
  )
}
